import { AppService } from "@suite/app.service";
import { Record } from "@common/data-models/records/record";
import { NewlyCreatedRecords } from "@common/data-models/newly-created-records/newly-created-records";
import { UserRecord } from "@common/data-models/records/user-record";
import { DataType } from '@common/enums/data-type';
import { DateTime } from "@common/data-models/complex-types/date-time";
import { FlatTreeControl } from "@angular/cdk/tree";
import { MatTreeFlatDataSource, MatTreeFlattener } from "@angular/material/tree";
import { BehaviorSubject, Observable, Subject } from "rxjs";

export interface TreeLikeNode<T> {
    id: string,
    label?: string,
    itemsCount?: number,
    object?: T,
    children: TreeLikeNode<T>[],
    nodeLastInGroup?: boolean,
    nodeFirstInGroup?: boolean,
    linkedRecordType?: number,
    isMetadata?: boolean,
    isNewItems?: boolean,
    cost?: Observable<number>,
    index?: number;
}

export interface TreeLikeFlatNode<T> {
    id: string,
    level: number,
    itemsCount?: number,
    hasChildren: boolean,
    label?: string,
    object?: T,
    nodeLastInGroup?: boolean,
    nodeFirstInGroup?: boolean,
    linkedRecordType?: number,
    isMetadata?: boolean,
    isNewItems?: boolean,
    cost?: Observable<number>,
    index?: number;
}


export class SearchResults {
    objectType?: number = null;

    static appService: AppService = null;
    static groupByField: string = null;
    static sortByField: any = null;
    static sortOrder: string = "asc"; // we want this to be "desc" by default, for dates. 
    static groupSortOrder: string = "asc"; // we want this to be "desc" by default, for dates. 

    static notFoundImageCache: { [id: string]: boolean } = {};
    static newlyAddedItems: NewlyCreatedRecords = null;

    users: Array<UserRecord> = [];

    byGuid: { [id: string]: Record } = {};
    newGuids: { [id: string]: boolean } = {};
    newItemsCount: number = 0;

    treeLikeData: TreeLikeNode<Record>[] = [];
    onDispose = new Subject<void>();

    cardItemHeights: number[] = [];
    condensedItemHeights: number[] = [];
    cardItemHeightsCustomFields: number[] = [];

    readonly treeControl: FlatTreeControl<TreeLikeFlatNode<Record>, string> =
        new FlatTreeControl<TreeLikeFlatNode<Record>, string>(this.getNodeLevel, this.getIsNodeExpandable, {
            trackBy: (node) => node.id
        });
    readonly treeDataSource: MatTreeFlatDataSource<TreeLikeNode<Record>, TreeLikeFlatNode<Record>>;


    readonly viewChange = new BehaviorSubject<{ start: number; end: number }>({
        start: 0,
        end: Number.MAX_VALUE,
    });


    constructor() {

        const treeFlattener = new MatTreeFlattener<TreeLikeNode<Record>, TreeLikeFlatNode<Record>, string>(
            this.nodeTransformer,
            this.getNodeLevel,
            this.getIsNodeExpandable,
            this.getNodeChildren
        );

        // Populates our flattened data into the tree control
        this.treeDataSource = new MatTreeFlatDataSource(
            this.treeControl,
            treeFlattener
        );
    }

    public addUser(user: UserRecord) {
        this.users.push(user);
        this.byGuid[user.guid] = user;
    }

    public remove(rec: Record) {
        if (this.byGuid[rec.guid]) {
            delete this.byGuid[rec.guid];
        }
    }

    public sortGroups(sortBy: any, sortOrder: string, groupSortOrder: string) {
        throw "sortGroups() not implemented";
    }

    public rebuildGroups(groupBy: string, sortBy: any, sortOrder: string, groupSortOrder: string) {
        throw "rebuildGroups() not implemented";
    }

    public dispose() {
        this.onDispose.next();
        SearchResults.notFoundImageCache = {};

        this.byGuid = {};
        this.newGuids = {};
        this.newItemsCount = 0;
    }

    public getGroups(): Array<string> {
        throw "getGroups() not implemented";
    }

    public getGroupCount(key: string): number {
        throw "getGroups() not implemented";
    }

    public getAllResults(): Array<Record> {
        throw "getAllResults() not implemented";
    }

    protected buildTree(groupBy: string): void {
        throw "buildFlatTree() not implemented";
    }

    public searchTimestamp: DateTime = null;

    public updateNewGuids(recGuids: Array<{ [id: string]: any }>, guidsToSkip?: { [id: string]: boolean }) {
        for (let rec of recGuids) {
            let timestamp = DataType.fromRaw(rec.CreatedDateTime, DataType.DATE) as DateTime;
            if (guidsToSkip && guidsToSkip[rec.guid]) {
                continue;
            }
            if ((!this.byGuid[rec.guid]) && this.searchTimestamp && (this.searchTimestamp?.get()?.valueOf() <= timestamp?.get()?.valueOf())) {
                if (!this.newGuids[rec.guid]) {
                    this.newGuids[rec.guid] = true;
                    this.newItemsCount++;
                }
            }
        }
    }

    // Function that maps a nested node to a flat node
    nodeTransformer(node: TreeLikeNode<Record>, level: number): TreeLikeFlatNode<Record> {
        return {
            id: node.id,
            label: node.label,
            index: node.index,
            itemsCount: node.itemsCount,
            level,
            hasChildren: node?.children?.length > 0,
            object: node.object,
            nodeLastInGroup: node.nodeLastInGroup,
            nodeFirstInGroup: node.nodeFirstInGroup,
            linkedRecordType: node?.linkedRecordType,
            isMetadata: node?.isMetadata,
            isNewItems: node?.isNewItems,
            cost: node?.cost
        };
    }

    // Function that gets a flat node's level
    getNodeLevel({ level }: TreeLikeFlatNode<Record>) {
        return level;
    }

    // Function that determines whether a flat node is expandable or not
    getIsNodeExpandable({ hasChildren }: TreeLikeFlatNode<Record>) {
        return hasChildren;
    }

    // Function that returns a nested node's list of children
    getNodeChildren({ children }: TreeLikeNode<Record>) {
        return children;
    }
}
